import { TextAreaField, TextField } from './fields.js';

/**
 * A bilingual content field: the Arabic and English values side by side.
 *
 * This is the interface half of the `name_ar` / `name_en` column convention
 * (PLAN section 12.3). Test names, methods, specifications, services, client
 * names and report content all carry both values, and both are entered by a
 * person.
 *
 * TWO RULES THIS COMPONENT EXISTS TO ENFORCE
 *
 * 1. NOTHING IS AUTO-TRANSLATED. Typing an English name does not populate the
 *    Arabic one, and never will without an explicit business rule (CLAUDE.md
 *    section 7). A laboratory's Arabic name is not a translation of its English
 *    name; it is a second fact.
 *
 * 2. CONTENT DIRECTION IS NOT LAYOUT DIRECTION. Every other component in this
 *    library takes its direction from the document root and must never set
 *    `dir` itself. This one is the deliberate exception: the Arabic input holds
 *    Arabic text and the English input holds Latin text REGARDLESS of which
 *    language the interface is in, so each INPUT carries its own `dir` (the
 *    label around it keeps the page's). Without it,
 *    an English name typed while the interface is Arabic renders with its
 *    punctuation at the wrong end.
 *
 * The two halves stack on a phone and sit side by side from `sm` upwards.
 */

export interface BilingualTextFieldProps {
  /** Already translated. */
  readonly labelAr: string;
  readonly labelEn: string;
  readonly valueAr: string;
  readonly valueEn: string;
  readonly onChangeAr: (value: string) => void;
  readonly onChangeEn: (value: string) => void;
  readonly descriptionAr?: string;
  readonly descriptionEn?: string;
  readonly errorAr?: string;
  readonly errorEn?: string;
  /**
   * A problem with the pair as a whole ("at least one language"): both halves
   * are marked and the message is said once, beneath them.
   */
  readonly error?: string;
  readonly isRequired?: boolean;
  readonly isReadOnly?: boolean;
  readonly isDisabled?: boolean;
  /**
   * Several lines per language, for wording that runs to paragraphs (a
   * quotation's conditions). The halves then stack, so neither is squeezed
   * into a quarter of the page.
   */
  readonly rows?: number;
}

export function BilingualTextField({
  labelAr,
  labelEn,
  valueAr,
  valueEn,
  onChangeAr,
  onChangeEn,
  descriptionAr,
  descriptionEn,
  errorAr,
  errorEn,
  error,
  isRequired = false,
  isReadOnly = false,
  isDisabled = false,
  rows,
}: BilingualTextFieldProps) {
  const pairInvalid = Boolean(error);
  const pairMessage = error ? (
    <p className="text-tiny text-danger">{error}</p>
  ) : null;
  if (rows !== undefined) {
    const half = (
      direction: 'rtl' | 'ltr',
      label: string,
      value: string,
      onChange: (value: string) => void,
      description: string | undefined,
      halfError: string | undefined,
    ) => (
      <div className="min-w-0">
        <TextAreaField
          textDirection={direction}
          label={label}
          value={value}
          onChange={onChange}
          rows={rows}
          {...(description ? { description } : {})}
          {...(halfError ? { error: halfError } : {})}
          isInvalid={pairInvalid}
          isRequired={isRequired}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
        />
      </div>
    );
    return (
      <div className="grid min-w-0 grid-cols-1 gap-stack">
        {half('rtl', labelAr, valueAr, onChangeAr, descriptionAr, errorAr)}
        {half('ltr', labelEn, valueEn, onChangeEn, descriptionEn, errorEn)}
        {pairMessage}
      </div>
    );
  }
  return (
    <div className="grid min-w-0 grid-cols-1 gap-stack sm:grid-cols-2">
      {/*
        Content direction, set per half ON THE INPUT. It used to be set on a
        wrapper, which turned the label and the required mark too: on an
        English page "Subject (Arabic)" sat right-aligned over its box, on an
        Arabic page "Subject (English)" left-aligned (UX pass). Only the text a
        person types belongs to the other direction.
      */}
      <div className="min-w-0">
        <TextField
          textDirection="rtl"
          label={labelAr}
          value={valueAr}
          onChange={onChangeAr}
          {...(descriptionAr ? { description: descriptionAr } : {})}
          {...(errorAr ? { error: errorAr } : {})}
          isInvalid={pairInvalid}
          isRequired={isRequired}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
        />
      </div>
      <div className="min-w-0">
        <TextField
          textDirection="ltr"
          label={labelEn}
          value={valueEn}
          onChange={onChangeEn}
          {...(descriptionEn ? { description: descriptionEn } : {})}
          {...(errorEn ? { error: errorEn } : {})}
          isInvalid={pairInvalid}
          isRequired={isRequired}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
        />
      </div>
      {pairMessage ? <div className="sm:col-span-2">{pairMessage}</div> : null}
    </div>
  );
}
